<template>
    <NuxtLink :to="volunteerLink" class="volunteer-card shrink-0 w-[300px] block cursor-pointer">
        <!-- Image -->
        <div class="relative h-[220px] rounded-t-3xl overflow-hidden bg-gray-200 dark:bg-surface-subtle">
            <CThumbnailImage
                v-if="volunteer.imageUrl"
                :src="volunteer.imageUrl"
                :alt="volunteer.title"
                type="project"
                class="size-full !aspect-auto !rounded-none"
            />
            <div v-else class="size-full flex items-center justify-center bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-900/30">
                <span class="text-4xl">❤️</span>
            </div>
            <!-- Match Score Badge -->
            <div
                v-if="volunteer.matchScore"
                class="absolute top-3 right-3 bg-white/90 dark:bg-surface-card/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm"
            >
                <span class="text-sm font-semibold text-green-600 dark:text-green-400">{{ volunteer.matchScore }}%</span>
            </div>
        </div>

        <!-- Content -->
        <div class="bg-white dark:bg-surface-card p-4 rounded-b-3xl flex flex-col gap-3">
            <div class="flex flex-col gap-3">
                <!-- Title & Organization -->
                <div class="flex flex-col gap-1">
                    <h3 class="font-semibold text-lg text-[#1d191f] dark:text-white tracking-tight leading-normal line-clamp-2">
                        {{ volunteer.title }}
                    </h3>
                    <p class="text-base text-[#6c5d73] dark:text-gray-300 leading-normal">
                        {{ volunteer.organization }}
                    </p>
                </div>

                <!-- Location -->
                <p v-if="volunteer.location" class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5">
                    <span class="text-[#047274] dark:text-primary-400">#</span>{{ volunteer.location }}
                </p>

                <!-- Tags -->
                <div class="flex flex-wrap gap-2">
                    <span
                        v-for="tag in volunteer.tags"
                        :key="tag"
                        class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5"
                    >
                        <span class="text-[#047274] dark:text-primary-400">#</span>{{ tag }}
                    </span>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'

interface Volunteer {
    title: string
    organization: string
    imageUrl?: string
    location?: string
    tags: string[]
    slug?: string
    matchScore?: number
}

const props = defineProps<{
    volunteer: Volunteer
}>()

const volunteerLink = computed(() => {
    if (props.volunteer.slug) {
        return `/dobrovolnictvo/dobrovolnicke-projekty/${props.volunteer.slug}`
    }
    // Fallback to title-based slug
    const slug = props.volunteer.title.toLowerCase().replace(/\s+/g, '-')
    return `/dobrovolnictvo/dobrovolnicke-projekty/${slug}`
})
</script>

<style scoped>
.volunteer-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.volunteer-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.volunteer-card :deep(.CThumbnailImage) {
    aspect-ratio: auto;
    border-radius: 0;
}
</style>
